import axios from 'axios';

// Nexus cutover 2026-05-25 (Phase F):
//   - Gateway moved from legacy K-Stack at gateway-dev.kstack.app to Nexus
//     at nexus-dev.kstack.app.
//   - App-key header renamed: x-kstack-app-key → x-nexus-app-key
//     (matches Nexus's dispatcher.ts authorization shape).
//   - REACT_APP_BOLUSNOTES_KEY is now a `nxsk_…` value minted via
//     `admin-mint-app-key.ts --app bolus_notes`. The legacy 'bolusnotes-web-dev'
//     fallback string is preserved for accidents but won't authenticate.
const GATEWAY_URL = process.env.REACT_APP_GATEWAY_URL || 'https://nexus-dev.kstack.app';

const api = axios.create({
  baseURL: GATEWAY_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add a request interceptor to inject the JWT token and App Key
api.interceptors.request.use(
  (config) => {
    // Inject the App Key
    config.headers['x-nexus-app-key'] = process.env.REACT_APP_BOLUSNOTES_KEY || 'bolusnotes-web-dev';

    const storedTokens = localStorage.getItem('bolusnotes_auth_tokens');
    if (storedTokens) {
      try {
        const tokens = JSON.parse(storedTokens);
        if (tokens.accessToken) {
          config.headers.Authorization = `Bearer ${tokens.accessToken}`;
        }
      } catch (err) {
        console.error('Error parsing stored tokens in API interceptor', err);
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor — 401 handling.
// BOLUS_NOTES-4 (2026-09-16): try ONE refresh via POST /auth/refresh, persist the
// rotated pair (Nexus refresh tokens are single-use with family revocation — a
// stale token on a retry logs the user out), then replay the request. Auth
// routes themselves are never refreshed/retried. Only after refresh fails do
// we drop the session and send the user to /login.
let refreshInFlight: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  const stored = localStorage.getItem('bolusnotes_auth_tokens');
  if (!stored) return null;
  let tokens: any;
  try { tokens = JSON.parse(stored); } catch { return null; }
  if (!tokens?.refreshToken) return null;
  try {
    const res = await axios.post(
      `${GATEWAY_URL}/auth/refresh`,
      { refreshToken: tokens.refreshToken },
      { headers: { 'x-nexus-app-key': process.env.REACT_APP_BOLUSNOTES_KEY || '' } },
    );
    const next = { ...tokens, ...res.data };
    localStorage.setItem('bolusnotes_auth_tokens', JSON.stringify(next));
    return next.accessToken || null;
  } catch {
    return null;
  }
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config || {};
    const url: string = originalRequest.url || '';
    const isAuthRoute = url.startsWith('/auth') || url.includes('/auth/');

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthRoute) {
      originalRequest._retry = true;
      if (!refreshInFlight) {
        refreshInFlight = refreshAccessToken().finally(() => { refreshInFlight = null; });
      }
      const newToken = await refreshInFlight;
      if (newToken) {
        originalRequest.headers = { ...(originalRequest.headers || {}), Authorization: `Bearer ${newToken}` };
        return api(originalRequest);
      }
      console.warn('Session expired and refresh failed, redirecting to login');
      localStorage.removeItem('bolusnotes_auth_tokens');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

/**
 * BolusNotes Specific API Helpers
 */

/**
 * BolusNotes Unified API — Nexus dispatch.
 * Calls the `bolusnotes_api` function on Nexus (appId: bolus_notes).
 *
 * Nexus cutover 2026-05-25 (Phase F):
 *   - Endpoint: legacy `/v1/jobs/bolus-notes/trigger/bolusnotes-api`
 *               → new    `/v1/app/bolus_notes/fn/bolusnotes_api`
 *   - Body shape: legacy `{ payload: { action, payload } }`
 *                 → new    `{ action, payload }`  (handler reads ctx.request.body
 *                          directly — no outer wrap)
 *   - Return shape: legacy `res.data.result`
 *                   → new    Nexus envelopes as `{ success, message, data }`,
 *                            so unwrap `res.data.data` for the actual handler
 *                            return value. (See dispatcher.ts: bare return values
 *                            are wrapped in the AquaStar/K-Stack response shape.)
 */
const triggerBolusJob = async (action: string, payload?: any) => {
  const res = await api.post('/v1/app/bolus_notes/fn/bolusnotes_api', {
    action,
    payload,
  });
  return res.data?.data ?? res.data?.result ?? res.data;
};

export const bolusnotesApi = {
  /** Extract intent from text via AI/Regex (delegated to App Job) */
  analyzeIntent: async (text: string) => {
    return triggerBolusJob('analyze_intent', { text });
  },

  /** Get glucose statistics (delegated to App Job) */
  getStats: async (hours: number = 24) => {
    const endDate = new Date().toISOString();
    const startDate = new Date(Date.now() - hours * 60 * 60 * 1000).toISOString();
    return triggerBolusJob('list_entries', { startDate, endDate }); // Simplified for stats logic in job
  },

  /**
   * Unified deployment for BolusNotes — DISABLED post-Nexus-cutover.
   * Nexus deployment is operator-only via `tsx admin-register-handlers.ts`
   * + `nexus admin apply-app-schema`. Removed `/v1/app/.../deploy` because
   * Nexus does not expose a self-service deploy endpoint to SPAs.
   */
  deploy: async (_version: string = '1.0.3') => {
    throw new Error(
      'deploy() is no longer available — Nexus deployment is operator-only. ' +
      'See admin-register-handlers.ts + admin-apply-app-schema.ts on kiss-nas.',
    );
  },

  /**
   * Generic SQL Query — DISABLED post-Nexus-cutover.
   * Nexus does not expose raw-SQL passthrough to SPAs. Use the typed RPC
   * actions on bolusnotes_api / bolusnotes_analytics instead.
   */
  query: async (_sql: string, _params?: Record<string, any>) => {
    throw new Error(
      'query() is no longer available post-Nexus-cutover. ' +
      'Use the action-dispatch helpers (bolusnotesApi.entries.list, ' +
      'bolusnotesAnalytics.*, etc.) instead.',
    );
  },

  /** 
   * CRUD for Bolus Entries (delegated to App Job)
   */
  dashboard: {
    get: async () => {
      return triggerBolusJob('get_dashboard_data');
    }
  },
  entries: {
    list: async (startDate?: string, endDate?: string, profileId?: string) => {
      return triggerBolusJob('list_entries', { startDate, endDate, profileId });
    },
    create: async (entry: any) => {
      return triggerBolusJob('create_entry', entry);
    },
    update: async (id: string, entry: any) => {
      return triggerBolusJob('update_entry', { ...entry, id });
    },
    delete: async (id: string) => {
      return triggerBolusJob('delete_entry', { id });
    }
  },

  /**
   * Multi-User Profiling
   */
  profiles: {
    list: async () => {
      return triggerBolusJob('list_profiles');
    },
    create: async (profile: any) => {
      return triggerBolusJob('create_profile', profile);
    },
    update: async (id: string, data: any) => {
      return triggerBolusJob('upsert', { table: 'users', id, data });
    }
  },

  /**
   * Dexcom Testing & Deduplication
   */
  dexcom: {
    test: async (username: string, password: string) => triggerBolusJob('test_dexcom', { username, password }),
    checkShared: async (username: string) => triggerBolusJob('check_dexcom_username', { username }),
    getAudit: async (username: string) => triggerBolusJob('get_dexcom_audit_logs', { username }),
    /**
     * Store the Dexcom Share password. BOLUS_NOTES-8: the password is
     * NEVER part of a profile upsert — `users.dexcom_share_password` no
     * longer exists. This action writes it encrypted into the platform
     * credentials store as `bolus_notes-dexcom-<kstackUserId>[-<profileId>]`,
     * which is the only place dexcom-poll reads from.
     */
    setCredentials: async (args: {
      username: string;
      password: string;
      server?: string;
      profile_id?: string;
    }) => triggerBolusJob('set_dexcom_credentials', args),
  },

  /**
   * Notebook Pages
   */
  notebooks: {
    list: async (profileId?: string, startDate?: string, endDate?: string) => {
      return triggerBolusJob('list_notebooks', { profileId, startDate, endDate });
    },
    get: async (pageId: string) => {
      return triggerBolusJob('get_notebook_page', { pageId });
    },
    update: async (id: string, pageData: any) => {
      return triggerBolusJob('upsert', { table: 'notebook_pages', id, data: pageData });
    }
  },

  /**
   * Journal (BOLUS_NOTES-17): one ordered stream of bolus / basal / note
   * entries per profile + local day. Pages are laid out by the client.
   */
  journal: {
    list: async (profileId: string, from: string, to: string) =>
      triggerBolusJob('list_journal', { profileId, from, to }),
    days: async (profileId: string, days = 90) =>
      triggerBolusJob('list_journal_days', { profileId, days }),
    drawing: async (profileId: string, entry_date: string) =>
      triggerBolusJob('get_day_drawing', { profileId, entry_date }),
    /** One write built by pages/notebookWrites (save_* / create_entry / update_entry / delete_*). */
    call: async (action: string, payload: Record<string, any>) => triggerBolusJob(action, payload),
    mealPhoto: async (id: string) => triggerBolusJob('get_meal_photo', { id }),
    /** 2026-10-12: meal photo carb analysis on the home-lab vision model (its own function; can take a minute). */
    analyzeMealPhoto: async (photo_data: string, hint?: string) => {
      const res = await api.post('/v1/app/bolus_notes/fn/bolusnotes_vision', { action: 'analyze_meal_photo', payload: { photo_data, ...(hint ? { hint } : {}) } }, { timeout: 150000 });
      return res.data?.data ?? res.data;
    },
    notePhoto: async (id: string) => triggerBolusJob('get_note_photo', { id }),
  },

  /**
   * Page Entries (Notes, Basal, Bolus Additions)
   */
  notes: {
    save: async (note: any) => triggerBolusJob('save_note', note),
    delete: async (id: string) => triggerBolusJob('delete_note', { id }),
    photo: async (id: string) => triggerBolusJob('get_note_photo', { id }),
  },
  basal: {
    save: async (entry: any) => triggerBolusJob('save_basal', entry),
    delete: async (id: string) => triggerBolusJob('delete_basal', { id }),
  },
  bolus: {
    save: async (entry: any) => {
      if (entry.id) return triggerBolusJob('update_entry', entry);
      return triggerBolusJob('create_entry', entry);
    }
  },

  /**
   * Follower Management (Delegated to App Job / Core APIs)
   */
  followers: {
    list: async () => {
      return triggerBolusJob('list_followers');
    },
    invite: async (email: string, permissionLevel: 'Viewer' | 'Contributor') => {
      return triggerBolusJob('invite_follower', { email, permissionLevel });
    },
    revoke: async (id: string) => {
      return triggerBolusJob('revoke_follower', { id });
    },
    accept: async (invitationCode: string) => {
      return triggerBolusJob('accept_invitation', { invitationCode });
    }
  },

  /**
   * Application File Services API
   */
  files: {
    upload: async (path: string, file: File) => {
      const formData = new FormData();
      formData.append('path', path);
      formData.append('file', file);
      const res = await api.post('/v1/files/upload', formData);
      return res.data;
    },
    presign: async (path: string) => {
      const res = await api.get('/v1/files/presign', {
        params: { path },
      });
      return res.data; // { url: string, expires_at: string }
    }
  }
};

export default api;
